export type SiteBuilderShareNetwork =
    | "email"
    | "facebook"
    | "instagram"
    | "link"
    | "linkedin"
    | "pinterest"
    | "tiktok"
    | "whatsapp"
    | "x"
    | "youtube";

export type SiteBuilderShareData = {
    title: string;
    url: string;
};

export const SITE_BUILDER_DEFAULT_SHARE_NETWORKS = [
    "facebook",
    "x",
    "linkedin",
    "whatsapp",
    "pinterest",
    "email",
] as const satisfies readonly SiteBuilderShareNetwork[];

/**
 * Resolves the canonical network from an icon, label or legacy URL.  The
 * persisted block remains template-neutral; each visual skin can keep its own
 * icon implementation while sharing the same public action.
 */
export function siteBuilderShareNetwork(value: string): SiteBuilderShareNetwork {
    const normalized = value.trim().toLowerCase().replace(/^social:/u, "");

    if (normalized.includes("facebook") || normalized === "fb") return "facebook";
    if (/(?:^|\s)x(?:\s|$)/u.test(normalized) || normalized.includes("twitter") || normalized.includes("x.com")) return "x";
    if (normalized.includes("linkedin")) return "linkedin";
    if (normalized.includes("whatsapp") || normalized.includes("wa.me")) return "whatsapp";
    if (normalized.includes("pinterest")) return "pinterest";
    if (normalized.includes("e-mail") || normalized.includes("email") || normalized.includes("mailto:")) return "email";
    if (normalized.includes("instagram")) return "instagram";
    if (normalized.includes("youtube") || normalized.includes("youtu.be")) return "youtube";
    if (normalized.includes("tiktok")) return "tiktok";

    return "link";
}

/**
 * Builds a real share action for icons without an authored href.  Explicit
 * destinations always win, so existing snapshots and intentionally custom
 * actions keep working unchanged.
 */
export function siteBuilderShareTarget(
    network: SiteBuilderShareNetwork,
    explicitHref: string | null | undefined,
    data: SiteBuilderShareData,
): string {
    const authoredHref = explicitHref?.trim() ?? "";

    if (authoredHref !== "" && authoredHref !== "#") {
        if (/^(?:data|javascript|vbscript):/iu.test(authoredHref)) {
            return "";
        }

        return authoredHref;
    }

    const url = data.url.trim();

    if (url === "") {
        return "";
    }

    const title = data.title.trim();
    const encodedTitle = encodeURIComponent(title);
    const encodedUrl = encodeURIComponent(url);

    if (network === "facebook") return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    if (network === "x") return `https://twitter.com/intent/tweet?url=${encodedUrl}${encodedTitle ? `&text=${encodedTitle}` : ""}`;
    if (network === "linkedin") return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    if (network === "whatsapp") return `https://wa.me/?text=${encodeURIComponent([title, url].filter(Boolean).join(" "))}`;
    if (network === "pinterest") return `https://pinterest.com/pin/create/button/?url=${encodedUrl}${encodedTitle ? `&description=${encodedTitle}` : ""}`;
    if (network === "email") return `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;

    return url;
}

export function siteBuilderShareTargetOpensNewWindow(href: string): boolean {
    return /^https?:\/\//iu.test(href);
}
